import { EntityType } from "./EntityType";

/**
 * Base Entity Interface
 * Common properties shared by all entity types
 * This interface helps manage overlapping contexts and provides type safety
 */
export interface BaseEntity {
    /** Unique identifier for the entity */
    id: string;

    /** Entity type */
    entityType: EntityType;

    /** Description of what this entity does */
    description?: string;

    /** Whether this entity is enabled/active */
    enabled?: boolean;

    /** UI-specific properties */
    ui?: {
        /** Position in the visual editor */
        position?: {
            x: number;
            y: number;
        };
        /** Whether the node is expanded in the UI */
        isExpanded?: boolean;
        /** Visual theme/color for the node */
        theme?: string;
    };

    /** Generated code for this entity */
    generatedCode?: string;

    /** Code generation status */
    codeGenerationStatus?: 'pending' | 'in_progress' | 'completed' | 'error';

    /** Error message if code generation failed */
    codeGenerationError?: string;

    /** File path where this entity will be generated */
    filePath?: string;

    /** File name for this entity */
    fileName?: string;
}

/**
 * Entity with Dependencies
 * Base interface for entities that can have dependencies
 */
export interface EntityWithDeps extends BaseEntity {
    /** Service IDs required by this entity */
    servicesRequired?: string[];

    /** Model IDs required by this entity */
    modelsRequired?: string[];

    /** Function IDs required by this entity */
    functionIds?: string[];

    /** Middleware IDs required by this entity */
    middlewareIds?: string[];
}

/**
 * Type guard to check if an entity has a specific property
 */
export function hasProperty<T extends keyof any>(
    entity: unknown,
    property: T
): entity is Record<T, unknown> {
    return typeof entity === 'object' && entity !== null && property in entity;
}

/**
 * Type guard to check if an entity is a BaseEntity
 */
export function isBaseEntity(entity: unknown): entity is BaseEntity {
    return (
        typeof entity === 'object' &&
        entity !== null &&
        'id' in entity &&
        'entityType' in entity
    );
}

/**
 * Type guard to check if an entity has dependencies
 */
export function hasEntityDependencies(entity: unknown): entity is EntityWithDeps {
    return (
        isBaseEntity(entity) &&
        (hasProperty(entity, 'servicesRequired') ||
            hasProperty(entity, 'modelsRequired') ||
            hasProperty(entity, 'functionIds') ||
            hasProperty(entity, 'middlewareIds'))
    );
}

/**
 * Helper to safely get entity ID
 */
export function getEntityId(entity: unknown): string | null {
    if (isBaseEntity(entity)) {
        return entity.id;
    }
    if (hasProperty(entity, 'id') && typeof entity.id === 'string') {
        return entity.id;
    }
    return null;
}

/**
 * Helper to safely get entity name
 */
export function getEntityName(entity: unknown): string | null {
    if (hasProperty(entity, 'name') && typeof entity.name === 'string') {
        return entity.name;
    }
    if (hasProperty(entity, 'serviceName') && typeof entity.serviceName === 'string') {
        return entity.serviceName;
    }
    if (hasProperty(entity, 'middlewareName') && typeof entity.middlewareName === 'string') {
        return entity.middlewareName;
    }
    return null;
}
